import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import { SelectChangeEvent, Stack } from '@mui/material'
import { MouseEvent } from 'react'
import { useParams } from 'react-router-dom'

import { useBookingContext } from '@providers/booking/booking_context'
import { i18WithParams as t } from '@shared/locale'
import { capitalize, getNthOfThis } from 'components-ui/src/shared/common'

export const RoomSelector = ({
  id,
  groupIdx,
  petIdx,
  prefix,
}: {
  id: string
  groupIdx: number
  petIdx: number
  prefix: string
}) => {
  const ctx = useBookingContext()

  const { serviceTypeName = '' } = useParams()

  const isBoarding = serviceTypeName === 'boarding'
  const isGrooming = serviceTypeName === 'grooming'

  const group = ctx.state.groups[groupIdx]
  const totPets = group.pets.length
  const normalizedRoom = group.pets[petIdx].room

  const label = isBoarding
    ? capitalize(t('global.label.room'))
    : isGrooming
      ? t('searchBar.petSelect.grooming')
      : t('searchBar.petSelect.attending')

  const onToggleChangeHandler = (_event: MouseEvent<HTMLElement>, value: number | null) => {
    if (value === null) return

    ctx.dispatch({
      type: 'SET_PET_IN_ROOM',
      payload: { groupIdx, petIdx, value },
    })
  }

  const onSelectChangeHandler = (event: SelectChangeEvent<number>) => {
    ctx.dispatch({
      type: 'SET_PET_IN_ROOM',
      payload: {
        groupIdx,
        petIdx,
        value: Number(event.target.value),
      },
    })
  }

  return isBoarding ? (
    <FormControl sx={{ minWidth: 140, mb: 0 }}>
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select
        labelId={`${id}-label`}
        id={`pet-room-select-${petIdx}`}
        value={ctx.state.groups[groupIdx].pets[petIdx].room}
        onChange={onSelectChangeHandler}
        label={label}
        autoWidth
      >
        {getNthOfThis(totPets, (i) => (
          <MenuItem key={i} value={i + 1}>
            <span data-testid={`pet-room-option-${i}`}>{`${prefix} Room ${i + 1}`}</span>
          </MenuItem>
        ))}

        <MenuItem value={0}>{t('global.label.notStaying')}</MenuItem>
      </Select>
    </FormControl>
  ) : (
    <Stack direction="column" spacing={2} justifyContent="space-between" alignItems="center">
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <FormControl variant="standard">
        <ToggleButtonGroup
          id={`pet-room-select-${petIdx}`}
          value={normalizedRoom}
          exclusive
          onChange={onToggleChangeHandler}
          aria-labelledby={`${id}-label`}
          size="small"
          sx={(theme) => ({
            border: '1px solid',
            borderColor: theme.palette.divider,
            borderRadius: '12px',
            overflow: 'hidden',
            backgroundColor: theme.palette.common.white,

            '& .MuiToggleButtonGroup-grouped': {
              border: 0,
              borderRadius: 0,
              width: 40,
              height: 40,
              paddingInline: theme.spacing(3),
              textTransform: 'none',
              fontWeight: 500,
              color: theme.palette.primary.main,
            },

            '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
              borderLeft: '1px solid',
              borderColor: theme.palette.divider,
            },

            '& .MuiToggleButton-root.Mui-selected': {
              backgroundColor: theme.palette.primary.shade12 ?? theme.palette.action.selected,
              color: theme.palette.primary.main,
            },

            '& .MuiToggleButton-root.Mui-selected:hover': {
              backgroundColor: theme.palette.primary.shade12 ?? theme.palette.action.selected,
            },
          })}
        >
          <ToggleButton value={0}>{t('global.label.no')}</ToggleButton>

          <ToggleButton value={1}>{t('global.label.yes')}</ToggleButton>
        </ToggleButtonGroup>
      </FormControl>
    </Stack>
  )
}
